import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { userManagementAPI, modulePermissionAPI, authAPI } from "../../services/api";
import { useAlert } from "../../Hooks/useAlert";
import Alert from "../common/Alert";
import ConfirmDialog from "../common/ConfirmDialog";
import Spinner from "../common/Spinner";
import BackButton from "./SettingsSections/BackButton";

const MODULE_GROUPS = [
  { label: "Home", code: "HOME", icon: "fa-home", color: "blue" },
  { label: "Dashboard", code: "DASHBOARD", icon: "fa-chart-line", color: "emerald" },
  { label: "Report - Report", code: "REPORT.REPORT", icon: "fa-file-alt", color: "violet", group: "Report & Billing" },
  { label: "Report - Bill", code: "REPORT.BILL", icon: "fa-file-invoice-dollar", color: "violet", group: "Report & Billing" },
  { label: "Settings - General", code: "SETTINGS.GENERAL", icon: "fa-cog", color: "blue", group: "Settings" },
  { label: "Settings - Customer Management", code: "SETTINGS.CUSTOMER", icon: "fa-users", color: "orange", group: "Manage" },
  { label: "Settings - Master Management", code: "SETTINGS.MASTER", icon: "fa-database", color: "orange", group: "Manage" },
  { label: "Settings - Sales & Expenses", code: "SETTINGS.SALES", icon: "fa-chart-pie", color: "orange", group: "Manage" },
  { label: "Settings - User Management", code: "SETTINGS.USERMGMT", icon: "fa-user-shield", color: "blue", group: "Settings" },
  { label: "Employee Management", code: "SETTINGS.EMPLOYEE", icon: "fa-user-tie", color: "blue" },
];

const ALL_MODULE_CODES = MODULE_GROUPS.map(m => m.code);

const colorMap = {
  blue: "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800",
  violet: "bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:border-violet-800",
  orange: "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
};

const groupIcons = { "Report & Billing": "fa-file-alt", Settings: "fa-cog", Manage: "fa-tasks" };
const groupLabels = { "Report & Billing": "Report & Billing", Settings: "Settings", Manage: "Manage" };
const groupColors = { "Report & Billing": "violet", Settings: "blue", Manage: "orange" };

function ModuleCheckboxGroup({ values, onChange }) {
  const groups = {};
  MODULE_GROUPS.forEach(m => {
    const g = m.group || "General";
    if (!groups[g]) groups[g] = [];
    groups[g].push(m);
  });

  return Object.entries(groups).map(([groupName, items]) => (
    <div key={groupName}>
      {groupLabels[groupName] !== undefined && (
        <div className="flex items-center gap-2 mb-2 pb-1 border-b border-gray-200 dark:border-slate-700">
          <i className={`fas ${groupIcons[groupName] || "fa-circle"} text-${groupColors[groupName] || "gray"}-500 text-xs`} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{groupLabels[groupName]}</span>
        </div>
      )}
      <div className="grid grid-cols-1 gap-1.5 mb-3">
        {items.map(m => (
          <label key={m.code}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-all ${
              values[m.code]
                ? (colorMap[m.color] || colorMap.blue) + " shadow-sm"
                : "bg-gray-50 dark:bg-slate-700/50 border-gray-200 dark:border-slate-600 hover:bg-gray-100 dark:hover:bg-slate-700"
            }`}>
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs ${
              values[m.code]
                ? "bg-white/80 text-gray-700"
                : "bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-gray-300"
            }`}>
              <i className={`fas ${m.icon}`} />
            </div>
            <span className={`flex-1 text-xs font-medium ${
              values[m.code] ? "text-gray-800 dark:text-gray-100" : "text-gray-600 dark:text-gray-300"
            }`}>{m.label}</span>
            <input type="checkbox" checked={!!values[m.code]}
              onChange={e => onChange(m.code, e.target.checked)}
              className="w-4 h-4 rounded accent-blue-600 cursor-pointer" />
          </label>
        ))}
      </div>
    </div>
  ));
}

function RoleManagementPage() {
  const navigate = useNavigate();
  const { showAlert, showConfirm, alertConfig, confirmConfig } = useAlert();
  const currentUser = authAPI.getCurrentUser();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [permUser, setPermUser] = useState(null);
  const [permValues, setPermValues] = useState({});
  const [permLoading, setPermLoading] = useState(false);

  const [templateValues, setTemplateValues] = useState({});
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateModal, setTemplateModal] = useState(false);

  useEffect(() => {
    if (!currentUser || (currentUser.userId !== 1 && currentUser.userName?.toLowerCase() !== "admin")) {
      showAlert({ type: "error", title: "Access Denied", message: "Admin access required" }).then(() => navigate("/settings"));
      return;
    }
    loadUsers();
    loadDefaultTemplate();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userManagementAPI.getAllUsers();
      if (response?.success && response?.data?.users) {
        setUsers(response.data.users.filter(u => u.userId !== 1 && u.userName?.toLowerCase() !== "admin"));
      } else if (Array.isArray(response?.data)) {
        setUsers(response.data.filter(u => u.userId !== 1 && u.userName?.toLowerCase() !== "admin"));
      } else if (response?.users) {
        setUsers(response.users.filter(u => u.userId !== 1 && u.userName?.toLowerCase() !== "admin"));
      }
    } catch (err) {
      console.error("Error loading users:", err);
    } finally {
      setLoading(false);
    }
  };

  const openPermModal = async (user) => {
    setPermUser(user);
    setPermLoading(true);
    try {
      const resp = await modulePermissionAPI.getUserPermissions(user.userId);
      const perms = resp?.data?.permissions || [];
      const values = {};
      ALL_MODULE_CODES.forEach(c => { values[c] = false; });
      perms.forEach(p => { if (values.hasOwnProperty(p.moduleCode)) values[p.moduleCode] = p.isEnabled; });
      setPermValues(values);
    } catch (err) {
      console.error(err);
    } finally {
      setPermLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    const permArray = Object.entries(permValues).map(([moduleCode, isEnabled]) => ({ moduleCode, isEnabled }));
    try {
      setPermLoading(true);
      const resp = await modulePermissionAPI.setUserPermissions(permUser.userId, permArray);
      if (resp?.success) {
        await showAlert({ type: "success", title: "Success", message: "Permissions saved" });
        setPermUser(null);
      }
    } catch (err) {
      await showAlert({ type: "error", title: "Error", message: err.response?.data?.message || "Failed" });
    } finally {
      setPermLoading(false);
    }
  };

  const loadDefaultTemplate = async () => {
    try {
      setTemplateLoading(true);
      const resp = await modulePermissionAPI.getDefaultTemplate();
      const perms = resp?.data || [];
      const values = {};
      ALL_MODULE_CODES.forEach(c => { values[c] = false; });
      perms.forEach(p => { if (values.hasOwnProperty(p.moduleCode)) values[p.moduleCode] = p.isEnabled; });
      setTemplateValues(values);
    } catch (err) {
      console.error("Error loading default template:", err);
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    const permArray = Object.entries(templateValues).map(([moduleCode, isEnabled]) => ({ moduleCode, isEnabled }));
    try {
      setTemplateSaving(true);
      const resp = await modulePermissionAPI.setDefaultTemplate(permArray);
      if (resp?.success) {
        await showAlert({ type: "success", title: "Success", message: "Default template saved" });
        setTemplateModal(false);
      }
    } catch (err) {
      await showAlert({ type: "error", title: "Error", message: err.response?.data?.message || "Failed to save template" });
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleToggleStatus = async (user) => {
    const action = user.activeStatus ? "deactivate" : "activate";
    const confirmed = await showConfirm({
      type: "warning", title: `${action === "activate" ? "Activate" : "Deactivate"} User`,
      message: `Are you sure you want to ${action} "${user.userName}"?`,
      confirmText: action === "activate" ? "Activate" : "Deactivate", cancelText: "Cancel"
    });
    if (!confirmed) return;
    try {
      setLoading(true);
      const resp = await userManagementAPI.toggleUserStatus(user.userId, !user.activeStatus);
      if (resp === true || resp?.success === true) {
        await showAlert({ type: "success", title: "Success", message: `User ${action}d` });
        await loadUsers();
      }
    } catch (err) {
      await showAlert({ type: "error", title: "Error", message: err.response?.data?.message || `Error ${action}ing user` });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u =>
    u.userName?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const enabledCount = Object.values(templateValues).filter(Boolean).length;

  return (
    <div className="settings-page">
      <header className="settings-page-header">
        <BackButton to="/settings" />
        <h1 className="settings-page-title">Role Management</h1>
      </header>

      <main className="settings-page-content flex flex-col overflow-hidden gap-3">
        {/* Default Template Bar */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shrink-0">
              <i className="fas fa-copy text-white text-sm" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-800 dark:text-white truncate">Default User Module Template</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {enabledCount} of {MODULE_GROUPS.length} modules &middot; applies to new users
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-1">
              {MODULE_GROUPS.slice(0, 4).map(m => (
                <span key={m.code} className={`w-5 h-5 rounded flex items-center justify-center text-[9px] ${
                  templateValues[m.code]
                    ? "bg-primary text-white"
                    : "bg-gray-200 dark:bg-slate-700 text-gray-400"
                }`}>
                  <i className={`fas ${m.icon}`} />
                </span>
              ))}
              <span className="text-xs text-gray-400 dark:text-gray-500 ml-0.5">+{Math.max(0, MODULE_GROUPS.length - 4)}</span>
            </div>
            <button onClick={() => setTemplateModal(true)}
              className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white text-xs font-semibold rounded-lg transition shadow-sm">
              <i className="fas fa-pen mr-1" /> Configure
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="settings-search-bar">
          <i className="fas fa-search" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by username or email..." className="settings-search-input" />
        </div>

        {/* User Table */}
        <div className="settings-table-card flex-1 min-h-0 flex flex-col overflow-hidden mb-0">
          {loading ? <div className="p-8"><Spinner /></div> : (
            <div className="settings-table-wrapper flex-1 overflow-y-auto min-h-0">
              <table className="settings-table">
                <thead className="sticky top-0 z-10">
                  <tr>
                    <th className="settings-table-th settings-table-th-center w-28">Actions</th>
                    <th className="settings-table-th">ID</th>
                    <th className="settings-table-th">Username</th>
                    <th className="settings-table-th settings-table-th-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length > 0 ? filteredUsers.map((user, i) => (
                    <tr key={user.userId} className={`settings-table-tr ${i % 2 === 1 ? "settings-table-tr-zebra" : ""}`}>
                      <td className="settings-table-td settings-table-td-center">
                        <div className="settings-table-actions">
                          <button onClick={() => openPermModal(user)}
                            className="settings-action-btn settings-action-btn-edit" title="Manage Permissions">
                            <i className="fas fa-shield-alt" />
                          </button>
                          <button onClick={() => handleToggleStatus(user)}
                            className={`settings-action-btn ${user.activeStatus ? "settings-action-btn-delete" : "settings-action-btn-approve"}`}
                            title={user.activeStatus ? "Deactivate" : "Activate"}>
                            <i className={`fas ${user.activeStatus ? "fa-ban" : "fa-check"}`} />
                          </button>
                        </div>
                      </td>
                      <td className="settings-table-td">{user.userId}</td>
                      <td className="settings-table-td">{user.userName}</td>
                      <td className="settings-table-td settings-table-td-center">
                        <span className={`px-1.5 py-0.5 inline-flex text-[10px] font-semibold rounded-full ${user.activeStatus ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                          {user.activeStatus ? "Active" : "Inactive"}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="4" className="settings-table-td text-center py-12">
                      <i className="fas fa-users text-4xl mb-3 block opacity-50 mx-auto" /><p className="text-sm">No users found</p>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {alertConfig && <Alert {...alertConfig} />}
      {confirmConfig && <ConfirmDialog {...confirmConfig} />}

      {/* User Permission Modal */}
      {permUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="fas fa-shield-alt text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-white text-sm font-bold">Permissions</h3>
                  <p className="text-white/70 text-xs">{permUser.userName}</p>
                </div>
              </div>
              <button onClick={() => setPermUser(null)} className="text-white/80 hover:text-white text-lg"><i className="fas fa-times" /></button>
            </div>
            <div className="p-5 max-h-96 overflow-y-auto">
              {permLoading ? <div className="flex justify-center py-8"><Spinner /></div> : (
                <ModuleCheckboxGroup values={permValues} onChange={(code, checked) => setPermValues(p => ({ ...p, [code]: checked }))} />
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/80 flex gap-2 justify-end">
              <button onClick={() => setPermUser(null)} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 rounded-lg transition">Cancel</button>
              <button onClick={handleSavePermissions} disabled={permLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg transition disabled:opacity-50 flex items-center gap-2">
                {permLoading ? <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Saving</> : <><i className="fas fa-check" /> Save</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Default Template Modal */}
      {templateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="fas fa-copy text-white text-sm" />
                </div>
                <div>
                  <h3 className="text-white text-sm font-bold">Default User Module Template</h3>
                  <p className="text-white/70 text-xs">Auto-assigned to newly registered users</p>
                </div>
              </div>
              <button onClick={() => setTemplateModal(false)} className="text-white/80 hover:text-white text-lg"><i className="fas fa-times" /></button>
            </div>
            <div className="p-5 max-h-96 overflow-y-auto">
              {templateLoading ? <div className="flex justify-center py-8"><Spinner /></div> : (
                <ModuleCheckboxGroup values={templateValues} onChange={(code, checked) => setTemplateValues(p => ({ ...p, [code]: checked }))} />
              )}
              <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 flex items-center gap-1">
                <i className="fas fa-info-circle" /> Changes apply only to future registrations. Existing users are not affected.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/80 flex items-center justify-between">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {!templateLoading && (
                  <>{Object.values(templateValues).filter(Boolean).length} of {MODULE_GROUPS.length} modules enabled</>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setTemplateModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-slate-700 hover:bg-gray-300 dark:hover:bg-slate-600 rounded-lg transition">Cancel</button>
                <button onClick={handleSaveTemplate} disabled={templateSaving || templateLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg transition disabled:opacity-50 flex items-center gap-2">
                  {templateSaving ? <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Saving</> : <><i className="fas fa-save" /> Save</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoleManagementPage;
